import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    FormGroupDirective,
    NgForm,
    Validators,
    FormControl,
    ReactiveFormsModule,
    FormsModule,
} from '@angular/forms';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { ErrorStateMatcher, MatOption } from '@angular/material/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subscription, map, startWith } from 'rxjs';
import { EnumAffinities } from 'src/app/models/enum-affinities';
import { EnumScopes } from 'src/app/models/enum-scopes';
import { EnumType } from 'src/app/models/enum-type';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { Property } from 'src/app/models/property';
import { PropertyDefinition } from 'src/app/models/property-definition';
import { ExternalsParametersService } from 'src/app/services/externals-parameters.service';
import { PropertyDefinitonsService } from 'src/app/services/property-definitons.service';
import { FormArrayFilterPipePipe } from 'src/app/pipes/form-array-filter-pipe.pipe';
import { MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxMatDatepickerInput, NgxMatDatetimepicker, NgxMatTimepickerComponent } from '@ngxmc/datetime-picker';

export class ErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

export class PropertyItem {
    name: string;
    display: string;
    isSelected: boolean;
}

@Component({
    selector: 'app-properties',
    templateUrl: './properties.component.html',
    styleUrls: ['./properties.component.css'],
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        ReactiveFormsModule,
        MatAutocomplete,
        MatOption,
        MatCheckbox,
        MatIconButton,
        MatTooltip,
        MatIcon,
        FormsModule,
        MatSuffix,
        MatSelect,
        MatPrefix,
        AsyncPipe,
        TranslateModule,
        FormArrayFilterPipePipe,
        MatDatepickerModule,
        NgxMatDatetimepicker,
        NgxMatDatepickerInput,
    ],
})
export class PropertiesComponent implements OnInit, OnDestroy {
    propertiesForm: UntypedFormGroup;
    availableProperties: PropertyDefinition[];
    unassignedProperties: Array<PropertyItem> = [];
    selectedProperties: string[] = [];
    subscriptionEvents: Subscription;
    managedScope = EnumScopes.Managed;
    propertyListControl = new FormControl();
    propertyList: Observable<PropertyItem[]>;

    matcher = new ErrorMatcher();

    searchProperty = '';

    @Input() properties: Property[];

    @Input() isProfile = false;

    @Input() affinity: EnumAffinities = EnumAffinities.Any;

    @Output() changed = new EventEmitter<Property[]>();

    @Output() isValid = new EventEmitter<boolean>();

    @ViewChild('propertyInput') propertyInput: ElementRef<HTMLInputElement>;

    isExternal = false;
    externalParameters: ExternalParameters;
    externalParametersChange: Subscription;

    private propertiesNotAffected: Property[] = [];

    constructor(
        private formbuilder: UntypedFormBuilder,
        private translateService: TranslateService,
        private propertyDefinitionsService: PropertyDefinitonsService,
        private spinner: NgxSpinnerService,
        private externalsParametersService: ExternalsParametersService
    ) {
        this.propertiesForm = this.formbuilder.group({
            propertiesGroup: this.formbuilder.array([]),
        });

        // Abonnement à l'évènement du changement de langue au service de traduction
        this.subscriptionEvents = translateService.onLangChange.subscribe((e: Event) => {
            this.loadDatas();
        });
    }

    ngOnInit() {
        this.loadDatas();

        this.externalParametersChange = this.externalsParametersService.externalParametersChange$.subscribe((parameters) => {
            if (parameters) {
                this.externalParameters = parameters;
                this.isExternal = true;
                this.properties = this.properties.filter((a) => parameters.properties.includes(a.name));
            }
        });
    }

    ngOnDestroy() {
        this.subscriptionEvents.unsubscribe();
        this.externalParametersChange.unsubscribe();
    }

    onChanges() {
        this.spinner.show();

        this.properties = [];

        // Application des changements des propriétés
        const currentProperties = this.propertiesForm.getRawValue().propertiesGroup as [{}];

        currentProperties.forEach((property: Property) => {
            if (property.type === EnumType.Date) {
                if (property.value !== null) {
                    property.value = new DatePipe(this.translateService.currentLang).transform(property.value, 'YYYY-MM-dd').toString();
                } else {
                    property.value = '';
                }
            } else if (property.type === EnumType.Datetime) {
                if (property.value !== null) {
                    property.value = new DatePipe(this.translateService.currentLang).transform(property.value, 'YYYY-MM-dd HH:mm:ss').toString();
                } else {
                    property.value = '';
                }
            } else {
                property.value = property.value?.toString();
            }

            this.properties.push(property);
        });

        this.properties = this.properties.concat(this.propertiesNotAffected);

        this.changed.emit(this.properties);
        this.isValid.emit(this.propertiesForm.valid);

        this.spinner.hide();
    }

    loadDatas() {
        this.spinner.show();

        // Initialise la collection
        this.unassignedProperties.splice(0, this.unassignedProperties.length);

        // this.propertiesService.getProperties(true).subscribe(confProperties => {
        this.propertyDefinitionsService.getPropertyDefinitions().subscribe((confProperties) => {
            // Affecte les propriétés existantes.
            // Si c'est une édition de profil, seules les propriétés de type "user" sont utilisées.
            // Ne prend pas les propriétés ayant pour scope "application" et "system"
            this.availableProperties = this.getAvailableProperties(this.affinity, confProperties);
            this.availableProperties.forEach((p) => {
                let property = new PropertyItem();
                property.name = p.name;
                property.display =
                    p.display.length > 0
                        ? p.display.findIndex((a) => a.language === this.translateService.currentLang) !== -1
                            ? p.display.find((t) => t.language === this.translateService.currentLang).value
                            : p.name
                        : p.name;
                property.isSelected = false;

                this.unassignedProperties.push(property);
            });

            const propertyForm = [];

            this.propertiesNotAffected = [];

            this.properties
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((propertyUser) => {
                    const indexUnassignedProperty = this.unassignedProperties.findIndex((property) => property.name === propertyUser.name);

                    if (indexUnassignedProperty >= 0) {
                        this.unassignedProperties.splice(indexUnassignedProperty, 1);
                    }

                    // Récupère la définition de propriété
                    const propertydefinition = this.availableProperties.find((p) => p.name === propertyUser.name);

                    if (propertydefinition) {
                        if (propertydefinition.type === EnumType.Datetime) {
                            propertyUser.value = new DatePipe(this.translateService.currentLang).transform(propertyUser.value, 'YYYY-MM-ddTHH:mm:ss').toString();
                        }
                        // Ne permet pas de modifier une propriétés herité en lecture seule ou une propriété de type "managed" si c'est une édition de profil
                        propertyForm.push(
                            this.formbuilder.group({
                                name: [propertyUser.name],
                                translateName: [this.getPropertyTranslate(propertyUser.name)],
                                value: [
                                    {
                                        value:
                                            propertydefinition.type === EnumType.Check
                                                ? Boolean(JSON.parse(propertyUser.value))
                                                : propertydefinition.type === EnumType.MList
                                                  ? propertyUser.value.split(',')
                                                  : propertyUser.value
                                                    ? propertyUser.value
                                                    : propertydefinition.default
                                                      ? propertydefinition.default
                                                      : propertyUser.value,
                                        disabled:
                                            propertydefinition.type === EnumType.Date ||
                                            propertydefinition.type === EnumType.Datetime ||
                                            (propertydefinition.readonly && propertyUser.legacy) ||
                                            (this.isProfile && propertydefinition.scope === EnumScopes.Managed)
                                                ? true
                                                : false,
                                    },
                                    Validators.pattern(propertydefinition.constraint),
                                ],
                                type: [propertydefinition?.type],
                                values: [propertydefinition.values],
                                scope: [propertydefinition.scope],
                                readonly: [{ value: propertydefinition.readonly, disabled: true }],
                                legacyable: [{ value: propertydefinition.legacyable, disabled: true }],
                                legacy: [{ value: propertyUser.legacy, disabled: true }],
                            })
                        );
                    } else {
                        this.propertiesNotAffected.push(propertyUser);
                    }
                });

            propertyForm;

            this.propertiesForm = this.formbuilder.group({
                propertiesGroup: this.formbuilder.array(propertyForm),
            });

            // Pour rendre visible toutes les propriétés invalides
            this.propertiesForm.markAllAsTouched();
            this.isValid.emit(this.propertiesForm.valid);

            // Tri la liste par le nom affiché
            this.unassignedProperties.sort((a, b) => a.display.localeCompare(b.display));

            this.propertyList = this.propertyListControl.valueChanges.pipe(
                startWith(null),
                map((property: string | null) => (property ? this.filterProperty(property) : this.unassignedProperties.slice()))
            );

            this.spinner.hide();
        });
    }

    /** Click sur le bouton pour ajouter une propriété */
    addProperty(propertiesName: string[]) {
        propertiesName.forEach((propertyName) => {
            const indexUnassignedProperty = this.unassignedProperties.findIndex((p) => p.name === propertyName);

            if (indexUnassignedProperty >= 0) {
                this.unassignedProperties.splice(indexUnassignedProperty, 1);
            }

            const propertiesGroup = this.propertiesForm.get('propertiesGroup') as UntypedFormArray;

            // Récupère la définition de propriété
            const propertydefinition = this.availableProperties.find((p) => p.name === propertyName);

            let dateValue: Date = null;

            if (propertydefinition.type === EnumType.Date || propertydefinition.type === EnumType.Datetime) {
                if (propertydefinition.default) {
                    dateValue =
                        propertydefinition.type === EnumType.Date
                            ? new Date(
                                  new DatePipe(this.translateService.currentLang).transform(new DatePipe('en').transform(propertydefinition.default, 'shortDate'), 'yyyy-MM-dd')
                              )
                            : new Date(
                                  new DatePipe(this.translateService.currentLang).transform(new DatePipe('en').transform(propertydefinition.default, 'short'), 'yyyy-MM-dd HH:mm')
                              );
                } else {
                    dateValue = new Date();
                }
            }

            propertiesGroup.push(
                this.formbuilder.group({
                    // name: [name],
                    name: [propertyName],
                    translateName: [this.getPropertyTranslate(propertyName)],
                    value: [
                        {
                            value:
                                propertydefinition.type === EnumType.Check
                                    ? Boolean(JSON.parse(propertydefinition.default))
                                    : propertydefinition.type === EnumType.Date || propertydefinition.type === EnumType.Datetime
                                      ? dateValue //new DatePipe(this.translateService.currentLang).transform(new DatePipe('en').transform(propertydefinition.default), 'shortDate')
                                      : propertydefinition.default
                                        ? propertydefinition.default
                                        : '',
                            disabled: propertydefinition.type === EnumType.Date || propertydefinition.type === EnumType.Datetime ? true : false,
                        },
                        Validators.pattern(propertydefinition.constraint),
                    ],
                    type: [propertydefinition?.type],
                    values: [propertydefinition.values],
                    scope: [indexUnassignedProperty >= 0 ? this.availableProperties.find((p) => p.name === propertyName).scope : EnumScopes.User],
                    readonly: [{ value: propertydefinition.readonly, disabled: true }],
                    legacyable: [{ value: propertydefinition.legacyable, disabled: true }],
                    legacy: [{ value: false, disabled: true }],
                })
            );
        });

        this.selectedProperties = [];

        this.onChanges();
    }

    /** Click sur le bouton pour supprimer une propriété */
    deleteProperty(propertiesName: string, index: number) {
        const currentProperties = this.propertiesForm.get('propertiesGroup') as UntypedFormArray;

        currentProperties.removeAt(index);

        const confProperty = this.availableProperties.find((p) => p.name === propertiesName);

        if (confProperty) {
            let property = new PropertyItem();
            property.name = confProperty.name;
            property.display =
                confProperty.display.length > 0
                    ? confProperty.display.findIndex((a) => a.language === this.translateService.currentLang) !== -1
                        ? confProperty.display.find((t) => t.language === this.translateService.currentLang).value
                        : confProperty.name
                    : confProperty.name;
            property.isSelected = false;

            this.unassignedProperties.push(property);
        }

        this.unassignedProperties.sort((a, b) => a.display.localeCompare(b.display));

        this.onChanges();
    }

    getAvailableProperties(affinity: EnumAffinities, propertyDefinitions: PropertyDefinition[]): PropertyDefinition[] {
        switch (affinity) {
            case EnumAffinities.User:
                return propertyDefinitions.filter(
                    (p) => (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) && (p.affinity === EnumAffinities.User || p.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Group:
                return propertyDefinitions.filter(
                    (p) => (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) && (p.affinity === EnumAffinities.Group || p.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Organization:
                return propertyDefinitions.filter(
                    (p) => (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) && (p.affinity === EnumAffinities.Organization || p.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Member:
                return propertyDefinitions.filter(
                    (p) => (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) && (p.affinity === EnumAffinities.Member || p.affinity === EnumAffinities.Any)
                );
                break;

            case EnumAffinities.Application:
                return propertyDefinitions.filter(
                    (p) => (p.scope === EnumScopes.User || p.scope === EnumScopes.Managed) && (p.affinity === EnumAffinities.Application || p.affinity === EnumAffinities.Any)
                );
                break;

            default:
                return propertyDefinitions;
                break;
        }
    }

    /**
     * Retourne la traduction de la propriété.
     * @param propertiesName Nom de la propriété.
     * @returns Retourne le nom de la propriété traduite.
     */
    getPropertyTranslate(propertiesName: string): string {
        try {
            return this.availableProperties.find((p) => p.name === propertiesName).display.find((t) => t.language === this.translateService.currentLang).value;
        } catch {
            return propertiesName;
        }
    }

    resetSearch() {
        this.searchProperty = '';
    }

    toggleSelection(property: PropertyItem) {
        property.isSelected = !property.isSelected;
        if (property.isSelected) {
            this.selectedProperties.push(property.name);
        } else {
            const i = this.selectedProperties.findIndex((x) => x === property.name);
            this.selectedProperties.splice(i, 1);
        }
    }

    private filterProperty(value: string): PropertyItem[] {
        const filterValue = value.toLowerCase();

        return this.unassignedProperties.filter((property) => property.display.toLowerCase().indexOf(filterValue) >= 0);
    }
}
